import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@vd/ui';
import { api } from '../../lib/api';
import { useAuth } from '../auth/AuthProvider';

/**
 * Where the extension stands for this account (TOADO-973), from the two
 * signals Toado has, each said for what it is (review round 1):
 *
 * - an extension connection for this account that has not been disconnected
 *   (`/me/extension-status`): an approval, not proof that this browser has the
 *   extension now, since uninstalling it does not disconnect it;
 * - the version last reported for this account (`extensionVersion` on `/me`),
 *   which can come from another install of it, another Chrome profile say.
 *
 * Clicking Install proves nothing, so it is never read as installed. A person
 * who says it is connected is shown as saying so, apart from what Toado sees.
 * The read is the signed-in person's own (its key carries their id), and Check
 * again asks for both signals afresh.
 */
export function ExtensionSetupStatus() {
  const { user, extensionVersion, refreshUser } = useAuth();
  const status = useQuery({
    queryKey: ['extension-status', user?.id ?? null],
    queryFn: () => api.getExtensionStatus(),
    enabled: !!user,
  });
  const [said, setSaid] = useState(false);
  const [checking, setChecking] = useState(false);
  /**
   * The version shown when it could not be read again on the last Check again (review round 2):
   * the note is about that version only, so a later refresh that brings another clears it (round 3).
   */
  const [staleVersion, setStaleVersion] = useState<string | null>(null);
  const version = extensionVersion.version;
  // The person's word and what was checked are theirs (review round 2): another account signed in here starts afresh.
  useEffect(() => {
    setSaid(false);
    setStaleVersion(null);
  }, [user?.id]);

  const checkAgain = async () => {
    setChecking(true);
    try {
      const [, me] = await Promise.allSettled([status.refetch(), refreshUser()]);
      setStaleVersion(me.status === 'rejected' && version ? version : null);
    } finally {
      setChecking(false);
    }
  };

  let line: string;
  let tone: 'good' | 'warn' | 'plain' = 'plain';
  if (status.isPending) line = 'Checking whether an extension is connected…';
  else if (status.isError) {
    line = 'Toado could not check the extension just now. Nothing else on Toado needs it: try again later.';
  } else if (status.data.installed) {
    const reported = version ? ` The version last reported for this account is ${version}` : '';
    if (version && extensionVersion.status === 'unsupported') {
      line = `An extension is connected to this account.${reported}, which is too old to use: update it from the Chrome Web Store, or remove it and install it again.`;
      tone = 'warn';
    } else if (version && extensionVersion.status === 'outdated') {
      line = `An extension is connected to this account.${reported}, which has an update: Chrome usually installs it within a day, or remove it and install it again to have it now.`;
      tone = 'warn';
    } else {
      line = `An extension is connected to this account.${version ? `${reported}.` : ''}`;
      tone = 'good';
    }
  } else {
    line = 'No extension is connected to this account yet. Install it, then press the Toado icon in your toolbar and Connect to Toado.';
    tone = 'warn';
  }

  return (
    <div
      data-testid="extension-status"
      data-tour-anchor="extension.status"
      style={{
        display: 'flex', flexDirection: 'column', gap: 8, padding: 12, marginBottom: 20, borderRadius: 8,
        border: '1px solid var(--border)',
        background: tone === 'good' ? 'var(--success-soft, var(--bg-muted))' : tone === 'warn' ? 'var(--warning-soft, var(--bg-muted))' : 'var(--bg-muted)',
        fontSize: 13, color: 'var(--text-primary)',
      }}
    >
      <strong style={{ fontSize: 13 }}>Your extension</strong>
      <span data-testid="extension-status-line" role="status">{line}</span>
      {staleVersion !== null && staleVersion === version && (
        <span data-testid="extension-status-version-stale" style={{ color: 'var(--text-secondary)' }}>
          The version could not be read again just now, so {version} is the one Toado had before.
        </span>
      )}
      {status.isSuccess && status.data.installed && (
        <span style={{ color: 'var(--text-secondary)' }}>
          That is an approval Toado holds, not a sight of this browser: an extension removed without disconnecting
          still counts. Its version can come from another install, in another Chrome profile say.
        </span>
      )}
      {status.isSuccess && !status.data.installed && (
        <span style={{ color: 'var(--text-secondary)' }}>
          If the extension says it is connected, it may be connected to another Toado account, or its connection may
          have ended. Open its Settings (the Toado icon, then Settings) and look under Account; this account is{' '}
          {user?.email ?? 'the one you are signed in with here'}. Sign out there and connect again to change it.
        </span>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button size="sm" variant="secondary" onClick={() => { void checkAgain(); }} disabled={checking || status.isFetching}>
          {checking || status.isFetching ? 'Checking…' : 'Check again'}
        </Button>
        {status.isSuccess && !status.data.installed && !said && (
          <Button size="sm" variant="ghost" onClick={() => setSaid(true)}>It is connected on my side</Button>
        )}
      </div>
      {said && status.isSuccess && !status.data.installed && (
        <span data-testid="extension-status-said" style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          You said it is connected. Toado has not seen a connection for this account, so this is your word, not a check.
        </span>
      )}
    </div>
  );
}
